import type { Modifier, Modifiers, SubstitutionModifier } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

export function generateSubstitutionsAndUpdateOthers(
  params: Params,
): Modifiers {
  const { additions, removals } = params;

  return additions.reduce<Modifiers>(generateSubstitutions, {
    additions: [],
    removals: [...removals],
    substitutions: [],
  });
}

// ─────────────────────────────────────────────────────────────────────────────

function generateSubstitutions(changes: Modifiers, addition: Modifier) {
  const { additions, removals, substitutions } = changes;
  const { subKind } = addition;

  if (!subKind) {
    return changes;
  }

  const maybeRemoval = geSameSubkindRemoval(subKind, removals);

  // NOTE: If there is no removal of the same kind, then there is no substitution.
  if (!maybeRemoval) {
    return { ...changes, additions: [...additions, addition] };
  }

  // Otherwise we can proceed by converting the corresponding removal to a substitution.

  const { removalIndex, removal } = maybeRemoval;

  const filteredRemovals = withoutModification(removals, removalIndex); // prettier-ignore
  const updatedSubstitutions = withSubstitution(substitutions, addition, removal); // prettier-ignore

  return {
    ...changes,
    removals: filteredRemovals,
    substitutions: updatedSubstitutions,
  };
}

function geSameSubkindRemoval(subKind: string, removals: Modifier[]) {
  const maybeSameKindRemovalIndex = removals.findIndex((removal) => removal.subKind === subKind); // prettier-ignore
  const maybeSameKindRemoval = removals[maybeSameKindRemovalIndex];

  if (maybeSameKindRemovalIndex === -1) {
    return null;
  }

  return {
    removalIndex: maybeSameKindRemovalIndex,
    removal: maybeSameKindRemoval,
  };
}

function withoutModification(
  modificationChanges: Modifier[],
  targetIndex: number,
) {
  return modificationChanges.filter((_, index) => index !== targetIndex);
}

function withSubstitution(
  substitutions: SubstitutionModifier[],
  addition: Modifier,
  removal: Modifier,
) {
  const substitution: SubstitutionModifier = {
    subKind: addition.subKind,
    addedIngredientId: addition.ingredientId,
    removedIngredientId: removal.ingredientId,
    additionCost: addition.additionCost,
    removalCost: removal.removalCost,
    substitutionCost: addition.substitutionCost,
  };

  return [...substitutions, substitution];
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  additions: Modifier[];
  removals: Modifier[];
};
